import { matchPath, type PathParam, useLocation } from 'react-router';

import { type RouteEnum } from '@/layouts/routes/Route.model.ts';

/**
 * URL params for every route pattern that declares them (see RouteEnum).
 * Derived from the patterns themselves, e.g. '/companies/:id' -> { id: string }.
 */
export type RouteTypedParams = {
  [Route in RouteEnum as RouteWithParamNames<Route>]: { [Key in RouteParamNames<Route>]: string };
};

/**
 * Names of the URL params declared by a route pattern
 * (e.g. '/companies/:id' -> 'id', '/company/:companyId/employee/:employeeId' -> 'companyId' | 'employeeId').
 */
type RouteParamNames<Pattern extends string> = PathParam<Pattern>;

/**
 * Params of a route as returned by the hook: present when the route matched
 * the current location, undefined for each param otherwise.
 */
type RouteParamsOf<Route extends keyof RouteTypedParams> = {
  [Key in RouteParamNames<Route>]?: string;
};

/**
 * Narrows a route pattern to one that actually declares params, so routes
 * without params are not part of RouteTypedParams.
 */
type RouteWithParamNames<Pattern extends string> = [RouteParamNames<Pattern>] extends [never]
  ? never
  : Pattern;

/**
 * Returns the URL params of the current location, typed for the given route
 * pattern(s). With multiple patterns the first match wins (mirroring the
 * `<Routes>` ranking; collisions only arise from overlapping prefixes/splats),
 * so a view mounted under more than one route can pass all of its patterns.
 * Params are resolved from the current pathname (useLocation), so the hook
 * answers "what is this route's param value on the current path", not "what
 * params the router passed to this route's context".
 */
export const useTypedParams = <Route extends keyof RouteTypedParams>(
  routes: Route | readonly Route[],
): RouteParamsOf<Route> => {
  const { pathname } = useLocation();
  const patterns: readonly Route[] = Array.isArray(routes) ? routes : [routes];
  const matched = patterns.map(pattern => matchPath(pattern, pathname)).find(it => it !== null);

  return matched?.params ?? {};
};
